import { Clickable } from '@euler/components/Clickable';
import { Icon } from '@euler/components/Icomoon';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo, ReactNode } from 'react';
import {
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';
import { useTheme } from 'styled-components';

export type CardProps = {
  containerStyle?: StyleProp<ViewStyle>;
  style?: StyleProp<ViewStyle>;
  title?: ReactNode;
  titleDecoratorView?: ReactNode;
  headerStyle?: StyleProp<ViewStyle>;
  titleTextStyle?: StyleProp<TextStyle>;
  headerRight?: ReactNode;
  headerRightArrow?: boolean;
  headerRightStyle?: StyleProp<ViewStyle>;
  headerRightTextStyle?: StyleProp<TextStyle>;
  bodyStyle?: StyleProp<ViewStyle>;
  noBodyPadding?: boolean;
  onHeaderRightPress?: () => void;
  children?: ReactNode;
};

export const Card = memo(
  ({
    title,
    titleDecoratorView,
    style,
    containerStyle,
    headerStyle,
    titleTextStyle,
    headerRight,
    headerRightArrow,
    headerRightStyle,
    headerRightTextStyle,
    bodyStyle,
    children,
    noBodyPadding,
    onHeaderRightPress,
  }: CardProps) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();
    const flattenedStyle = StyleSheet.flatten(style);
    return (
      <View
        css={`
          padding: 0;
          border-width: 0;
          border-radius: ${rpx(10)}px;
          margin-bottom: ${flattenedStyle?.marginBottom ?? 0}px;
        `}
        style={containerStyle}
      >
        <View
          css={`
            overflow: hidden;
            background-color: #fff;
            border-radius: ${rpx(10)}px;
            elevation: 10;
          `}
          style={[style, { marginBottom: 0 }]}
        >
          {/* card header */}
          {title || headerRight ? (
            <View
              css={`
                min-height: ${rpx(44)}px;
                padding-left: ${rpx(15)}px;
                padding-right: ${rpx(15)}px;
                flex-direction: row;
                justify-content: space-between;
                flex-wrap: nowrap;
                align-items: center;
                border-bottom-width: 1px;
                border-bottom-color: ${theme.components.card.borderColor};
              `}
              style={headerStyle}
            >
              <View
                css={`
                  flex-direction: row;
                  align-items: center;
                  justify-content: flex-start;
                  flex: 1;
                `}
              >
                {typeof title === 'string' || typeof title === 'number' ? (
                  <Text
                    css={`
                      font-family: ${FontFamily.NotoSans.Regular};
                      font-size: ${rpx(18)}px;
                      line-height: ${rpx(20)}px;
                      color: ${theme.components.card.titleColor};
                    `}
                    style={titleTextStyle}
                  >
                    {title}
                  </Text>
                ) : (
                  title
                )}
                {titleDecoratorView}
              </View>
              {headerRight && (
                <Clickable
                  onPress={onHeaderRightPress}
                  hitSlop={PredefinedHitSlops.large}
                  css={`
                    flex-direction: row;
                    align-items: center;
                    justify-content: flex-end;
                  `}
                  style={headerRightStyle}
                >
                  {typeof headerRight === 'string' ||
                  typeof headerRight === 'number' ? (
                    <Text
                      css={`
                        font-family: ${FontFamily.NotoSans.Light};
                        font-size: ${rpx(14)}px;
                        line-height: ${rpx(16)}px;
                        color: ${onHeaderRightPress
                          ? theme.link
                          : theme.components.card.headerRightColor};
                      `}
                      style={headerRightTextStyle}
                    >
                      {headerRight}
                    </Text>
                  ) : (
                    headerRight
                  )}
                  {headerRightArrow ? (
                    <Icon
                      name="exclamation-circle-o"
                      size={rpx(13)}
                      color={
                        onHeaderRightPress
                          ? theme.link
                          : theme.components.card.headerRightColor
                      }
                      css={`
                        margin-left: ${rpx(5)}px;
                        margin-right: ${rpx(-4)}px;
                        height: ${rpx(16)}px;
                      `}
                    />
                  ) : null}
                </Clickable>
              )}
            </View>
          ) : null}
          {/* card body */}
          <View
            css={`
              padding: ${noBodyPadding ? 0 : rpx(15)}px;
            `}
            style={bodyStyle}
          >
            {children}
          </View>
        </View>
      </View>
    );
  },
);
